'use server'

import { after } from 'next/server'
import { revalidatePath } from 'next/cache'
import { createClient } from '@/lib/supabase/server'
import { createAdminClient } from '@/lib/supabase/admin'
import { getWordExample } from '@/lib/deepseek'
import { normalizeTr } from '@/lib/tr-text'

// «Мои слова» (миграция 20260924170000_user_words): слово из «Как сказать?»
// → личная колода повторения рядом со словами курса.

export type SaveWordResult = { status: 'saved' | 'already' | 'in-course' | 'full' }

// Потолок колоды — от накрутки; живому ученику до него далеко.
const MAX_WORDS = 2000

export async function saveUserWord(input: {
  turkish: string
  russian: string
  note?: string
  // Готовый пример (фото-перевод отдаёт его вместе со словом): тогда второй
  // запрос в DeepSeek за примером не нужен.
  example?: { tr: string; ru: string }
}): Promise<SaveWordResult> {
  const supabase = await createClient()
  const { data: claims, error: claimsError } = await supabase.auth.getClaims()
  if (claimsError || !claims?.claims) throw new Error('Unauthorized')
  const userId = claims.claims.sub

  const turkish = input.turkish.replace(/\s+/g, ' ').trim().slice(0, 120)
  const russian = input.russian.replace(/\s+/g, ' ').trim().slice(0, 160)
  const note = input.note?.trim().slice(0, 80) || null
  if (!turkish || !russian) throw new Error('Пустое слово')
  const exTr = input.example?.tr.replace(/\s+/g, ' ').trim().slice(0, 300) ?? ''
  const exRu = input.example?.ru.replace(/\s+/g, ' ').trim().slice(0, 300) ?? ''
  const example = exTr && exRu ? { example_tr: exTr, example_ru: exRu } : {}

  // Слово курса, которое уже в повторении, второй раз не заводим.
  const normalized = normalizeTr(turkish)
  const pattern = normalized.replace(/[%_\\]/g, (c) => `\\${c}`)
  const { data: courseRows } = await supabase
    .from('user_vocab_progress')
    .select('vocab_id, vocabulary!inner(turkish)')
    .eq('user_id', userId)
    .ilike('vocabulary.turkish', pattern)
    .limit(5)
  const inCourse = ((courseRows ?? []) as unknown as { vocabulary: { turkish: string } | null }[]).some(
    (r) => r.vocabulary && normalizeTr(r.vocabulary.turkish) === normalized,
  )
  if (inCourse) return { status: 'in-course' }

  const { count } = await supabase.from('user_words').select('id', { count: 'exact', head: true }).eq('user_id', userId)
  if ((count ?? 0) >= MAX_WORDS) return { status: 'full' }

  const { data: inserted, error } = await supabase
    .from('user_words')
    .upsert({ user_id: userId, turkish, russian, note, ...example }, { onConflict: 'user_id,turkish', ignoreDuplicates: true })
    .select('id')
  if (error) throw new Error(error.message)
  const id = inserted?.[0]?.id as string | undefined
  if (!id) return { status: 'already' }
  if (exTr && exRu) {
    revalidatePath('/review/words')
    return { status: 'saved' }
  }

  // Пример фразы — после ответа: карточка «Как сказать?» не ждёт DeepSeek.
  // Не получилось — слово повторяется без примера, это не ошибка.
  after(async () => {
    try {
      const ex = await getWordExample({ turkish, russian })
      await createAdminClient()
        .from('user_words')
        .update({ example_tr: ex.exampleTr, example_ru: ex.exampleRu })
        .eq('id', id)
        .eq('user_id', userId)
    } catch (e) {
      console.warn('[user_words] пример не получен:', e instanceof Error ? e.message : e)
    }
  })

  revalidatePath('/review/words')
  return { status: 'saved' }
}

export async function deleteUserWord(id: string): Promise<void> {
  const supabase = await createClient()
  const { data: claims, error: claimsError } = await supabase.auth.getClaims()
  if (claimsError || !claims?.claims) throw new Error('Unauthorized')
  const { error } = await supabase.from('user_words').delete().eq('id', id).eq('user_id', claims.claims.sub)
  if (error) throw new Error(error.message)
  revalidatePath('/review/words')
}
